/* eslint-disable no-useless-escape */
import { ChangeDetectorRef, Component, ElementRef, HostListener, Inject, OnInit, ViewChild } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { FullScreenSysDownComponent, LoginComponent, ReminderOverlayComponent, ImageViewerComponent, AudioIndicatorComponent } from '@org/components';
import { GmLocationSwitcherComponent } from './gm-assignment/gm-location-switcher.component';
import { NotificationsComponent } from './notification/notifications.component';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { AuthService, HealthCheckService, TenantService, VendorsService, WebSocketService, SoundPermissionBannerComponent, environment } from '@org/shared-services';
import { UserService, TaskService } from '@org/shared-services';
import { distinctUntilChanged, filter, Observable, of, switchMap } from 'rxjs';
import { Task, User } from '@org/interfaces';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialogModule } from '@angular/material/dialog';
import { SwUpdate } from '@angular/service-worker';
import { MatSnackBar } from '@angular/material/snack-bar';
@Component({
    imports: [
        FormsModule, 
        CommonModule, 
        ReminderOverlayComponent, 
        FullScreenSysDownComponent,
        RouterModule, 
        NotificationsComponent, 
        MatSnackBarModule, 
        MatDialogModule,
        SoundPermissionBannerComponent,
        ImageViewerComponent,
        AudioIndicatorComponent,
        GmLocationSwitcherComponent
    ],
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit {
  title = 'BuziFlow - Task Management System';
  dropdownOpen = false;
  dropdownReports = false;
  reportsDropdownOpen = false;
  user!: any;
  tasks$!: Observable<Task[]>;
  totalTasks$: Observable<number>;
  currentUser$: Observable<User>;
  vendors$!: Observable<boolean>;
  mobileMenuOpen!: boolean;
  subletsDropdown = false;
  subletsMobileDropdownOpen = false;

   gmAdminDropdown = false;
   gmAdminMobileDropdownOpen = false;

  mobileAdminMenuOpen = false;
  
  tenantDetails?: string = '';
  tenantAddress?: string = '';

  public appVersion = environment.version;


  @ViewChild('dropdownreports') dropdownreports!: ElementRef<HTMLDivElement>;
  @ViewChild('dropdownmenu') dropdownmenu!: ElementRef<HTMLDivElement>;
  @ViewChild('dropdownsublets') dropdownsublets!: ElementRef;
  @ViewChild('dropdowngmadmin') dropdowngmadmin!: ElementRef;

  constructor(private http: HttpClient,
    @Inject(AuthService) private authService: AuthService,
    private taskService: TaskService,
    private userService: UserService,
    private vendorsService: VendorsService,
    private tenantService: TenantService,
    private ws: WebSocketService,
    private updates: SwUpdate,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private el: ElementRef,
    private router: Router) {
    this.tasks$ = this.taskService.tasks$;
    this.totalTasks$ = this.taskService.totalTasks$;
    this.currentUser$ = this.authService.currentUser as Observable<User>;
    this.vendors$ = this.vendorsService.vendors$;
  }

  ngOnInit(): void {
    // Hydrate session from cookies only when we do not already have a user (e.g. after login).
    if (!this.authService.isLoggedIn()) {
      this.authService.getCurrentUser().subscribe();
    }

    this.authService.isLoggedIn$
      .pipe(distinctUntilChanged())
      .subscribe((loggedIn) => {
        if (loggedIn) {
          setTimeout(() => this.ws.connect(), 300);
        } else {
          this.ws.disconnect();
        }
      });
    // Get tenant details from localStorage first
    const cachedTenant = localStorage.getItem('tenantDetails');
    if (cachedTenant) {
      const tenant = JSON.parse(cachedTenant);
      this.tenantDetails = tenant.nameProp;
      this.tenantAddress = tenant.formattedAddress;
    }
    this.authService.isLoggedIn$
      .pipe(
        filter(isLoggedIn => isLoggedIn === true),
        switchMap(() => {
          const tenantId = this.authService.currentUserValue?.tenant;
          return tenantId ? this.tenantService.getShopTenantDetails() : of(null);
        }),
        filter(tenant => tenant !== null)
      )
      .subscribe(tenant => {
        if (tenant) {
          localStorage.setItem('tenantDetails', JSON.stringify(tenant));
          this.tenantDetails = tenant.nameProp;
          this.tenantAddress = tenant.formattedAddress;
          this.cdr.detectChanges();
        }
      });
   this.currentUser$.subscribe(user => {
      if (user && user.name != undefined) {
        this.user = user;
      } else {
        this.user = this.authService.currentUserValue;
      }
      this.userService.setUser(user);

      this.dropdownOpen = false;
      this.cdr.detectChanges();
    });

    setInterval(() => {
      if (this.updates.isEnabled) {
        this.updates.checkForUpdate().then(() => {
         // console.log('Checked for updates');
        });
      }
    }, 60 * 1000); // Check every 6 hours (6 * 60 * 60 * 1000)

    if (this.updates.isEnabled) {
      this.updates.versionUpdates.subscribe(event => {
        if (event.type === 'VERSION_READY') {
          this.promptUserForUpdate(); // Notify user about update availability
  
          // Send a message to the service worker to show the notification
          if (navigator.serviceWorker && navigator.serviceWorker.controller) {
            navigator.serviceWorker.getRegistration().then(registration => {
              if (registration) {
                const newWorker = registration.installing;
                if (newWorker) {
                  newWorker.onstatechange = () => {
                    if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                      //console.log('New content is available; prompting for refresh.');
                      
                      // Send message to the service worker to show the notification
                      navigator.serviceWorker.controller?.postMessage({
                        type: 'NEW_VERSION_AVAILABLE'
                      });
                    }
                  };
                }
              }
            });
          }
        }
      });
    }

  }

  isTech(): boolean {
    return !this.authService.hasRole('tech');
  }



  // Function to generate initials
  getInitials(name: string): string {
    if (name != null) {
      const names = name.split(' ');
      const initials = names.map(n => n.charAt(0).toUpperCase()).join('');
      return initials.slice(0, 2); // Return only the first two initials
    }
    return '';
  }

  // Function to generate background color based on initials
  getBackgroundColor(initials: string): string {
    if (initials != null) {
      const colors = [
        'bg-red-500', 'bg-green-500', 'bg-blue-500', 'bg-yellow-500',
        'bg-purple-500', 'bg-pink-500', 'bg-indigo-500', 'bg-gray-500'
      ];
      const charCodeSum = initials
        .split('')
        .reduce((acc, char) => acc + char.charCodeAt(0), 0);
      return colors[charCodeSum % colors.length];
    }
    return '';
  }

  openDropdown(): void {
    this.dropdownOpen = true;
    this.cdr.detectChanges();
  }

  closeDropdown(): void {
    this.dropdownOpen = false;
    this.cdr.detectChanges();
  }

  toggleDropdown(event?: MouseEvent) {
    if (event) {
      event.stopPropagation();
    }
    this.dropdownOpen = !this.dropdownOpen;
    this.cdr.detectChanges();
  }

  toggleMobileMenu(): void {
    this.mobileMenuOpen = !this.mobileMenuOpen;
  }

  toggleDropDownReports() {
    this.dropdownReports = !this.dropdownReports;
  }

  // Close dropdown if clicked outside
   @HostListener('document:click', ['$event'])
   closeDropdownReports(event: Event) {
     
    if (this.dropdownreports && this.dropdownreports.nativeElement && !this.dropdownreports.nativeElement.contains(event.target as Node)) {
       this.dropdownReports = false;
    } 
   }
  
   // Close main dropdown if clicked outside
   @HostListener('document:click', ['$event'])
   closeDropdownMenu(event: Event) {
     if (this.dropdownOpen && this.dropdownmenu && this.dropdownmenu.nativeElement && !this.dropdownmenu.nativeElement.contains(event.target as Node)) {
       this.dropdownOpen = false;
     }
   }

   @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    
    // Check if click is on the dropdown toggle button
    const isDropdownButton = target.closest('#user-menu-button');
    // Check if click is inside the dropdown menu
    const isDropdownMenu = target.closest('#user-dropdown-menu');
    
    if (!isDropdownButton && !isDropdownMenu) {
      this.dropdownOpen = false;
      this.cdr.detectChanges();
    }
  }

  logOut(): void {
    this.ws.disconnect();
    this.authService.logout();
    if (this.mobileMenuOpen) {
      this.mobileMenuOpen = false;
    }

  }

  get isLoggedIn(): boolean {
    return this.authService.isLoggedIn();
  }

  get isGM(): boolean {
    return this.user?.roles?.includes('gm') || false;
  }

  // Function to prompt the user to update
  promptUserForUpdate() {
    const snackBarRef = this.snackBar.open('New version available', 'Refresh', {
      duration: 0
    });

    snackBarRef.onAction().subscribe(() => {
      window.location.reload(); // Refresh the app to activate the new service worker
    });
  }

  toggleMobileMenu2(): void {
    this.mobileMenuOpen = !this.mobileMenuOpen;
    // Close the reports dropdown if mobile menu is closing
    if (!this.mobileMenuOpen) {
      this.reportsDropdownOpen = false;
    }
  }

  toggleReportsDropdown(): void {
    this.reportsDropdownOpen = !this.reportsDropdownOpen;
  }

  toggleDropDownSublets() {
    this.subletsDropdown = !this.subletsDropdown;
    // Close other dropdowns if open
    this.dropdownOpen = false;
    this.dropdownReports = false;
  }

  closeDropdownSublets(event: Event) {
    event.stopPropagation();
  }

  toggleSubletsMobileDropdown() {
    this.subletsMobileDropdownOpen = !this.subletsMobileDropdownOpen;
    // Close other mobile dropdowns if open
    this.reportsDropdownOpen = false;
  }

  isSubletRouteActive(): boolean {
    const currentUrl = this.router.url;
    // Only return true if we're specifically on a sublet-related route
    return ['/vendor-tasks', '/vendor-invite'].some(path => 
      currentUrl.startsWith(path)
    );
  }

   // Add to existing click handler
   @HostListener('document:click', ['$event'])
   onClick(event: Event) {
     // ... existing click handling ...
     if (!this.dropdownsublets?.nativeElement.contains(event.target)) {
       this.subletsDropdown = false;
     }

     if (!this.dropdowngmadmin?.nativeElement.contains(event.target)) {
      this.gmAdminDropdown = false;
    }
   }

   openInviteVendor(event: Event) {
    event.stopPropagation();
    this.vendorsService.openInviteVendorPanel();
  }

  closeInviteVendorModal() {
    this.vendorsService.closeInviteVendorPanel();
  }

  // Add these methods
  toggleDropDownGMAdmin() {
    this.gmAdminDropdown = !this.gmAdminDropdown;
    // Close other dropdowns
    this.subletsDropdown = false;
    this.dropdownOpen = false;
    this.dropdownReports = false;
  }

  closeDropdownGMAdmin(event: Event) {
    event.stopPropagation();
  }

  toggleGMAdminMobileDropdown() {
    this.gmAdminMobileDropdownOpen = !this.gmAdminMobileDropdownOpen;
    // Close other mobile dropdowns
    this.subletsMobileDropdownOpen = false;
    this.reportsDropdownOpen = false;
  }

  isGMAdminRouteActive(): boolean {
    const currentUrl = this.router.url;
    return ['/team', '/vendor-approval'].some(path => 
      currentUrl.startsWith(path)
    );
  }

  toggleMobileAdminMenu() {
    this.mobileAdminMenuOpen = !this.mobileAdminMenuOpen;
  }

}

  